import { BlockStatus } from '@strength/domain';
import { MetricBenchmarkData } from '@strength/domain-workout';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { MdError, MdLink } from '@teamworksdev/icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DisplayWorkoutStatus,
  WorkoutStatusBadge,
  isWorkoutStatusDragBlocked,
} from '../../../components/badge/workout-status-badge';
import { cn } from '../../../components/utils';
import { useMetrics } from '../../../hooks/useMetrics';
import { WorkoutWithCycleAndSession } from '../types';
import { getDisplayNameForWorkout } from '../utils';
import {
  ExerciseDisplayGroup,
  getSectionExerciseGroups,
} from './exercise-display-utils';

type WorkoutCalendarCardProps = {
  workout: WorkoutWithCycleAndSession;
  status: DisplayWorkoutStatus;
  blockStatus?: BlockStatus;
  applyBenchmarkData: MetricBenchmarkData;
  onOpenPanel?: (workoutId: string) => void;
  isReadOnly?: boolean;
  isActive?: boolean;
  hasPublishError?: boolean;
  /** The workout's date, pre-formatted for its accessible name (SC-5441). */
  dragAccessibleDate?: string;
  /**
   * Drag interactions are enabled for this card (the flag and edit rights
   * allow). Listeners attach even when the workout's status renders the
   * card read-only: the drag start warns instead of the attempt failing
   * silently, and the drop never moves the workout.
   */
  draggable?: boolean;
  /** Saving state while a drag move for this workout is in flight. */
  isMoving?: boolean;
};

export function WorkoutCalendarCard({
  workout,
  status,
  blockStatus,
  applyBenchmarkData,
  onOpenPanel,
  isReadOnly,
  isActive,
  hasPublishError,
  dragAccessibleDate,
  draggable,
  isMoving,
}: WorkoutCalendarCardProps) {
  const { t } = useTranslation();
  const { getMetrics } = useMetrics();
  const metrics = useMemo(() => getMetrics(true), [getMetrics]);

  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: workout.workoutId,
    // Drag stays enabled for a status-read-only card while drag is on
    // (the host warns at drag start and rejects the move at drop); it is
    // disabled only when drag is off or the card cannot open the panel.
    disabled: !draggable || !onOpenPanel,
  });

  const title = workout.name ?? getDisplayNameForWorkout(workout);
  const sectionGroups = workout.sections.map((section) => ({
    id: section.workoutSectionId,
    groups: getSectionExerciseGroups(section, metrics, t, applyBenchmarkData),
  }));

  const cardClassName = cn(
    'relative block w-full overflow-hidden rounded-md border pl-2.5 pr-2 py-2 text-left text-xs text-inherit no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
    hasPublishError
      ? 'bg-red-50 hover:bg-red-100 border-red-200'
      : 'bg-gray-50 hover:bg-gray-100 border-gray-200',
    isActive && 'border-blue-500 ring-2 ring-blue-500',
    isMoving && 'opacity-50',
    // Drag preview affordances, mirroring the section drag: a grab cursor on
    // the draggable card, and the dragged card itself follows the pointer
    // (translate + elevation) so the coach sees the workout over the day it
    // would land on. The hovered day cell highlights underneath (see
    // DroppableDayCell). A drag-blocked card (read-only or in-progress
    // status) gets no grab cursor — its drag can only warn — but still
    // follows the pointer while dragged.
    draggable &&
      !isWorkoutStatusDragBlocked(status) &&
      !isDragging &&
      'cursor-grab',
    isDragging && 'cursor-grabbing z-30 shadow-lg ring-2 ring-blue-400',
  );

  const innerContent = (
    <>
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0 w-1',
          hasPublishError ? 'bg-red-500' : 'bg-gray-400',
        )}
      />
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="font-semibold text-gray-900 leading-tight">
          {title}
        </span>
        {hasPublishError && (
          <MdError
            className="shrink-0 text-red-600"
            size={12}
            title={t('blockHasPublishingErrors')}
          />
        )}
      </div>
      {status.type !== 'planned' && (
        <div className="mb-2">
          <WorkoutStatusBadge status={status} blockStatus={blockStatus} />
        </div>
      )}
      <div className="flex flex-col">
        {sectionGroups
          .filter((s) => s.groups.length > 0)
          .map((section, idx) => (
            <ul
              key={section.id}
              className={cn(
                'list-none m-0 p-0 space-y-1 text-gray-700',
                idx !== 0 && 'mt-2 pt-2 border-t border-gray-300',
              )}
            >
              {section.groups.map((group) => (
                <ExerciseGroupItem key={group.key} group={group} />
              ))}
            </ul>
          ))}
      </div>
    </>
  );

  if (!isReadOnly) {
    return (
      <button
        type="button"
        ref={draggable ? setDragRef : undefined}
        {...(draggable ? dragListeners : {})}
        {...(draggable ? dragAttributes : {})}
        onClick={() => onOpenPanel?.(workout.workoutId)}
        aria-label={
          dragAccessibleDate
            ? t('editWorkoutOnDate', { name: title, date: dragAccessibleDate })
            : t('editWorkout', { name: title })
        }
        aria-pressed={isActive}
        aria-busy={isMoving || undefined}
        className={cn(
          'cursor-pointer',
          draggable && 'touch-none',
          cardClassName,
        )}
        style={
          isDragging
            ? { transform: CSS.Translate.toString(transform) }
            : undefined
        }
      >
        {innerContent}
      </button>
    );
  }

  // A status-read-only card renders as a div (no click-to-edit), but while
  // drag is enabled it still carries the drag listeners so a drag attempt
  // reaches the host and warns — the drag start fires, the drop is rejected
  // (see resolveAllViewDragEnd), and the card snaps back.
  if (draggable) {
    return (
      <div
        ref={setDragRef}
        {...dragListeners}
        className={cn('touch-none', cardClassName)}
        style={
          isDragging
            ? { transform: CSS.Translate.toString(transform) }
            : undefined
        }
      >
        {innerContent}
      </div>
    );
  }

  return <div className={cardClassName}>{innerContent}</div>;
}

function ExerciseGroupItem({ group }: { group: ExerciseDisplayGroup }) {
  return (
    <li className="leading-tight">
      <div className="flex items-center gap-1 break-words">
        <span className="break-words">{group.name}</span>
        {group.isSuperset && (
          <MdLink
            aria-hidden
            className="ml-auto shrink-0 text-gray-500"
            size={12}
          />
        )}
      </div>
      {group.prescriptionLines.map((p, i) => (
        <div key={`${group.key}-${i}`} className="text-gray-500">
          {p}
        </div>
      ))}
    </li>
  );
}
